import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import styled, { css, withTheme } from 'styled-components';

import type { FullscreenLayoutProps } from '@typings/layout.ts';
import { Button } from '@ui/buttons/Button.tsx';
import { Icon } from '@ui/icons/Icon.tsx';
import Tooltip, { TooltipAnchor } from '@ui/tooltip';

import { SEARCH_VIEW_CATEGORY_LIST_ID } from '../../plugins/online/homeView/constants.ts';
import findByLayoutId from '../../utils/findByLayoutId.ts';
import { legacyBusPayload } from '../extModules/bustle.ts';
import { useCSSColors } from '../hooks/useCSSColors.ts';
import SpeedDial from './SpeedDial.tsx';

const Wrapper = styled.div`
  display: grid;
  grid-template-columns: 100%;
  grid-template-areas:
    'header'
    'app';
  grid-template-rows: auto 1fr;
  position: absolute;
  width: 100%;
  height: 100%;
  z-index: 10;
  overflow: hidden;

  &:has([data-route-preview-position='expanded']) [data-mobile-map-controls] {
    display: none;
  }

  &:has([data-route-preview-position]) [data-mobile-map-controls] {
    bottom: calc(var(--wf-layout-bottom-bar-height, 0px) + 10px);
  }
`;

const Map = styled.div`
  grid-area: main;
  height: 100%;
  z-index: 50;
  position: relative;
`;

const MobileControlsWrapper = styled.div<{ $isMobileReflow?: boolean }>`
  flex-direction: ${({ $isMobileReflow }) => ($isMobileReflow ? 'row' : 'column')};
  display: flex;
  align-items: center;
  justify-content: center;
`;

const FloatingComponent = styled.div`
  position: absolute;
  z-index: 150;
  height: auto;
  max-height: 100%;
  width: 100%;
  top: 0; /* default position, can be overridden by specific components */
  inset-inline-start: 0;
`;

const SidebarWrapper = styled(FloatingComponent)<{ $isFullscreen?: boolean }>`
  display: flex;
  flex-direction: column;
  width: auto;
  height: auto;
  transition: transform 0.5s ease-in-out;
  z-index: 152;

  .-desktop & {
    box-shadow: none;
    max-height: 100%;
    padding: min(20px, 2vw) min(20px, 2vw) max(42px, 5vh);
    inset-inline-start: 0;
    top: 0;
    pointer-events: none; /* Block all pointer events on wrapper */
    @media (max-height: 700px) {
      padding-bottom: max(42px, 5vh); // at high zoom let the container show more content while protecting AppInfo button
    }

    &::after {
      content: '';
      position: absolute;
      inset: auto 0 0 0;
      height: max(42px, 5vh);
      pointer-events: none; // Allow clicks to pass through bottom padding to AppInfo button
      z-index: 1;
    }
  }

  .-mobile & {
    height: ${({ $isFullscreen }) => ($isFullscreen ? '100%' : 'auto')};
    width: 100%;
  }
`;

const Sidebar = styled.div<{ $isFullscreen?: boolean; $isWide?: boolean }>`
  position: relative;
  display: flex;
  flex-direction: column;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  min-height: 0;
  border-radius: ${({ theme }) => theme.standardRadius};

  .-desktop & {
    width: ${({ $isWide }) => ($isWide ? 'min(375px, 42vw)' : 'min(320px, 35vw)')};
    max-height: calc(100% - 60px);
    pointer-events: auto; /* Re-enable pointer events for interactive content */
  }

  .-mobile & {
    width: 100%;
    max-height: 100%;
    height: ${({ $isFullscreen }) => ($isFullscreen ? '100%' : 'auto')};

    &:has([data-mobile-directions-pill]) {
      border-end-start-radius: 0;
      border-end-end-radius: 0;
    }

    /* Avoid Chrome to see Safari hack */
    @supports (-webkit-touch-callout: none) {
      /* The hack for Safari */
      max-height: -webkit-fill-available;
    }
  }
`;

const Header = styled.div<{ $isContentOpen?: boolean; $isPresentational?: boolean }>`
  grid-area: header;
  background-color: var(--color-widgetBackground);
  border-bottom: ${({ $isContentOpen }) => ($isContentOpen ? '1px solid #ebebeb' : '')};
  z-index: 2;
  display: flex;
  border-radius: ${({ theme }) => theme.standardRadius};
  /*
  Below style is just used when the UI is displaying Flight Status
  */
  ${({ $isContentOpen }) =>
    $isContentOpen &&
    `
    border-bottom-left-radius: 0;
    border-bottom-right-radius: 0;
  `}
  ${({ $isPresentational }) =>
    $isPresentational &&
    `
    display: none;
  `}

  .-mobile & {
    max-height: 60vh; /* max to 60% of the viewport height */
    overflow-y: auto;
    overflow-x: hidden;
    flex-shrink: 0; /* avoid showing scrollbar unless it reaches max-height */

    &:has([data-mobile-directions-pill]) {
      border-end-start-radius: 0;
      border-end-end-radius: 0;
    }
  }
`;
const Content = styled.div<{ $isContentOpen?: boolean; $isPresentational?: boolean }>`
  height: auto;
  background-color: var(--color-background);
  overflow: hidden;
  transition: 0.5s ease-in-out;
  display: flex;
  flex-direction: column;
  z-index: 1;
  border-radius: ${({ theme }) => theme.standardRadius};
  /*
  Below style is just used when the UI is displaying POI View, or any 'content' which includes the Category list view on splash page.
  */
  ${({ $isPresentational }) =>
    $isPresentational
      ? `
    border-top-left-radius: 8px;
    border-top-right-radius: 8px;
    `
      : `
    border-top-left-radius: 0px;
    border-top-right-radius: 0px;
    `}

  .-mobile & {
    height: 100%;
    flex-shrink: 1;
  }
`;
const ContentToggler = styled(Button)`
  display: none;

  .-desktop & {
    display: block;
    margin: auto;
    background-color: var(--color-mapNavBackground);
    color: var(--color-mapNavText);
    padding: 0 12px;
    border: none;
    border-radius: ${({ theme }) => theme.standardRadius};
    border-top-left-radius: 0;
    border-top-right-radius: 0;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
    position: relative;
    pointer-events: auto; /* Re-enable pointer events for compass button */
  }

  &:focus-visible {
    outline-offset: -3px;
  }
`;

const BottomBar = styled(FloatingComponent)`
  top: auto;
  bottom: 0;
  z-index: 160;
  transition: 0.2s ease-in-out height;
  box-sizing: border-box;

  /* Safari mobile fix - use fixed positioning to avoid viewport height bugs */
  .-mobile & {
    @supports (-webkit-touch-callout: none) {
      /* The hack for Safari - position fixed avoids absolute positioning bugs with viewport height */
      position: fixed;
      left: 0;
      right: 0;
      bottom: 0;
      padding-bottom: env(safe-area-inset-bottom, 0px);
    }
  }
`;

const ControlsWrapper = styled(FloatingComponent)`
  pointer-events: none;
  display: flex;
  flex-direction: column;
  height: 100%;
  width: auto;
  inset-inline-end: 0;
  z-index: 161;
  align-items: flex-end;
  justify-content: flex-end;
`;

const FeedbackZoneWrapper = styled.div`
  pointer-events: all;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  width: auto;
  .-desktop & {
    margin-top: 16px;
  }
  .-mobile & {
    margin-top: 0;
  }
`;

const ButtonOptionsWrapper = styled.div<{ $isHidden?: boolean; $isMobile?: boolean }>`
  align-items: flex-end;
  bottom: 0;
  display: ${({ $isHidden }) => ($isHidden ? 'none' : 'flex')};
  flex-direction: ${({ $isMobile }) => ($isMobile ? 'column' : 'column')};
  flex: 0 0 auto;
  margin-inline-start: auto;
  min-height: 1px;
  padding: min(20px, 2vw);
  pointer-events: all;
  inset-inline-end: 0;
  width: unset;
`;

const MapControlWrapper = styled.div`
  display: inline-flex;

  &:not(:first-child) {
    margin-top: 8px;
  }
`;

const LevelSelector = styled.div<{ $isHidden?: boolean; $isMobile?: boolean }>`
  ${({ $isHidden }) => ($isHidden ? 'display: none;' : 'display: flex;')}
  ${({ $isMobile }) => ($isMobile ? 'display: none;' : 'display: flex;')}
  align-items: flex-start;
  min-height: 1px;
  flex: 1 1 auto;
  pointer-events: none;
  padding-top: min(20px, 2vw);
  padding-inline-end: min(20px, 2vw);
`;

const MobileLevelSelector = styled(FloatingComponent)`
  pointer-events: none;
  height: 100%;
  min-height: 1px;
  flex: 1 1 auto;
  display: flex;
  align-items: flex-end;
`;

const FloatingBannerWrapper = styled.div`
  display: flex;
  justify-content: center;
  pointer-events: auto; /* Re-enable pointer events for floating banner */

  .locationButton {
    height: 56px;
    width: 56px;
    outline: none;
    border: none;
    background: #ffffff;
    position: absolute;
    bottom: 88px;
    inset-inline-end: 20px;
    border-radius: 100%;
  }
`;

// z-index value is 151 so it sits above the map
const MapOverlay = styled.div`
  z-index: 151;
  position: absolute;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
`;

// Top site/application header (landmark)
const TopHeader = styled.header`
  grid-area: header;
  z-index: 160;
  position: relative;
  width: 100%;
`;

// Primary content area (main landmark) containing the map and associated UI chrome
const MainWrapper = styled.main`
  grid-area: app;
  position: relative;
  display: grid;
  grid-template-columns: 100%;
  grid-template-areas: 'main';
  grid-template-rows: 1fr;
  height: 100%;
  width: 100%;
  z-index: 10;
  overflow: hidden;
`;

/** Space between the sidebar card and a top-placed mobile toast, in px. */
const MOBILE_TOAST_TOP_GAP = 12;

const MobileToastWrapper = styled.div<{ $placement: 'top' | 'bottom'; $topOffset: number }>`
  grid-area: 'main';
  position: absolute;
  width: 100%;
  display: flex;
  justify-content: center;
  background-color: transparent;
  ${({ $placement, $topOffset }) =>
    $placement === 'top'
      ? css`
          /* The toast is absolutely positioned, so it ignores padding; the gap has to live in the offset. */
          top: ${$topOffset + MOBILE_TOAST_TOP_GAP}px;
          padding: 0 26px;
          /* Don't block map gestures in the empty strip; only the toast itself is interactive. */
          pointer-events: none;
          & > * {
            pointer-events: all;
          }
        `
      : css`
          bottom: 0;
          pointer-events: all;
          padding: 26px 26px 40px;
          min-height: 30px;
        `}
`;

const MapCover = styled.div`
  width: 100%;
  height: 100%;
  position: absolute;
  z-index: 151;
  background-color: lightgray;
  pointer-events: none;
  font-size: 80px;
  font-weight: 500;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const FullscreenOnlyLayout = ({ app, children, uiHide, Loading, mapLoaded }: FullscreenLayoutProps) => {
  const colors = useCSSColors();
  const [isMapDisabled, setIsMapDisabled] = useState(false);
  const [isHeaderVisible, setIsHeaderVisible] = useState(true);
  const [isSearchConfirmed, setIsSearchConfirmed] = useState(false);
  const T = app.gt();
  const [isMobile, setIsMobile] = useState(app.env.isMobile());
  const [isMobileReflow, setisMobileReflow] = useState(app.env.isMobileReflow());
  const isDesktop = !isMobile;
  const [hideMapForVisualTest, setHideMapForVisualTest] = useState(false);
  // These refs are used to detect if bottom bar or sidebar are covering full screen
  const wrapperRef = useRef<HTMLDivElement>(null);
  const bottomBarRef = useRef<HTMLDivElement | null>(null);
  const sideBarRef = useRef<HTMLDivElement | null>(null);

  // Mobile starts with the map visible; showing categories here races header startup
  // and can leave a fullscreen panel covering the map controls.
  useEffect(() => {
    if (app.env.isDesktop()) {
      app.bus.send('layers/show', { id: SEARCH_VIEW_CATEGORY_LIST_ID });
    }
  }, []);

  useEffect(() => {
    const unsub = app.bus.on('map/disableMap', async ({ disable = true }) => {
      setIsMapDisabled(disable);
    });
    return unsub;
  }, []);

  useEffect(() => {
    const unsub = app.bus.on('map/visualTest', ({ hideMap }: { hideMap?: boolean }) =>
      setHideMapForVisualTest(!!hideMap),
    );
    return unsub;
  }, []);

  useEffect(() => {
    const unsub = app.bus.on('screen/displayHeader', async ({ display = true }) => {
      setIsHeaderVisible(display);
    });
    return unsub;
  }, []);

  // Mobile toasts sit at the bottom unless a toast opts in to `placement: 'top'`, which puts it just below the sidebar.
  const [toastPlacement, setToastPlacement] = useState<'top' | 'bottom'>('bottom');
  // Kept current by the resize observer (see checkMapVisible), since the sidebar renders after toast/show fires.
  const [sidebarHeight, setSidebarHeight] = useState(0);
  useEffect(() => {
    const unsub = app.bus.on('toast/show', ({ placement }: { placement?: 'top' | 'bottom' }) => {
      setToastPlacement(placement === 'top' ? 'top' : 'bottom');
    });
    return unsub;
  }, []);

  useEffect(() => {
    app.bus.monitor('headerOnline/updateSearchConfirmed', ({ isSearchConfirmed }: { isSearchConfirmed?: boolean }) =>
      setIsSearchConfirmed(!!isSearchConfirmed),
    );
  }, []);

  const [isMapVisible, setIsMapVisible] = useState(true);

  const checkMapVisible = useCallback(() => {
    let mapFullyCovered = false;
    const w = wrapperRef.current?.offsetWidth;
    const h = wrapperRef.current?.offsetHeight;
    if (w && h) {
      if (bottomBarRef.current && bottomBarRef.current.offsetWidth >= w && bottomBarRef.current.offsetHeight >= h) {
        // bottom bar covers full screen
        mapFullyCovered = true;
      } else if (sideBarRef.current && sideBarRef.current.offsetWidth >= w && sideBarRef.current.offsetHeight >= h) {
        // sidebar covers full screen
        mapFullyCovered = true;
      } else if (
        bottomBarRef.current &&
        sideBarRef.current &&
        bottomBarRef.current.offsetWidth >= w &&
        sideBarRef.current.offsetWidth >= w &&
        bottomBarRef.current.offsetHeight + sideBarRef.current.offsetHeight >= h
      ) {
        // bottom bar and sidebar together cover full screen
        mapFullyCovered = true;
      }
    }
    setIsMapVisible(!mapFullyCovered);
    setSidebarHeight(sideBarRef.current?.offsetHeight ?? 0);
    wrapperRef.current?.style.setProperty(
      '--wf-layout-bottom-bar-height',
      `${bottomBarRef.current?.getBoundingClientRect().height ?? 0}px`,
    );
  }, []);

  const resizeObserver = useMemo(() => {
    if (window.ResizeObserver) {
      return new ResizeObserver(checkMapVisible);
    }
  }, [checkMapVisible]);

  const getBottomBarRef = useCallback(
    (element: HTMLDivElement | null) => {
      if (bottomBarRef.current) {
        resizeObserver?.unobserve(bottomBarRef.current);
      }
      bottomBarRef.current = element;
      if (element) {
        resizeObserver?.observe(element);
      } else {
        checkMapVisible();
      }
    },
    [resizeObserver],
  );
  const getSideBarRef = useCallback(
    (element: HTMLDivElement | null) => {
      if (sideBarRef.current) {
        resizeObserver?.unobserve(sideBarRef.current);
      }
      sideBarRef.current = element;
      if (element) {
        resizeObserver?.observe(element);
      } else {
        checkMapVisible();
      }
    },
    [resizeObserver],
  );

  const getLayersShowing = async () => {
    const [visibleLayers = []] = await app.bus.send('layers/getVisibleLayersIds');
    const isPoiShowing = visibleLayers.includes('poiViewMobile') || visibleLayers.includes('poiViewDesktop');
    const isNavShowing =
      visibleLayers.includes('DirectionsSearchControls') ||
      visibleLayers.includes('DirectionsSearchView') ||
      visibleLayers.includes('DirectionsResultControls') ||
      visibleLayers.includes('DirectionsResultMobile');
    const isFlightStatusShowing = visibleLayers.includes('flightSearchView');
    return { isPoiShowing, isNavShowing, isFlightStatusShowing };
  };

  const toDesktop = useCallback(async () => {
    const { isPoiShowing, isNavShowing, isFlightStatusShowing } = await getLayersShowing();
    document.getElementById('tooltip')?.setAttribute('style', 'display: none;');

    if (!isPoiShowing && !isNavShowing && !isFlightStatusShowing) {
      app.bus.send('layers/showWithLayoutId', legacyBusPayload('content'));
      app.bus.send('headerOnline/hide');
      if (!isSearchConfirmed) {
        // restore custom actions if not searching
        const [contentLayers = []] = (await app.bus.send(
          'layers/getVisibleLayersByLayoutId',
          legacyBusPayload('content'),
        )) as string[][];
        const [previousLayer = ''] = (await app.bus.send('layers/getPreviousLayer')) as string[];
        // default to show category list if there is no previous layer
        app.bus.send('layers/show', {
          id: previousLayer || SEARCH_VIEW_CATEGORY_LIST_ID,
        });
        app.bus.send('layers/setPreviousLayer', legacyBusPayload(contentLayers[0] || ''));
      } else {
        app.bus.send('layers/show', { id: 'SearchResultsBody' });
      }
      app.bus.send('layers/show', { id: 'headerOnlineHome' });
    } else if (isPoiShowing) {
      app.bus.send('layers/show', { id: 'poiViewDesktop' });
    }
  }, [isSearchConfirmed]);

  const toMobile = useCallback(async () => {
    const { isPoiShowing, isNavShowing, isFlightStatusShowing } = await getLayersShowing();
    document.getElementById('tooltip')?.setAttribute('style', 'display: none;');

    app.bus.send('map/changePadding', { padding: 'mol' });
    if (!isPoiShowing && !isNavShowing && !isFlightStatusShowing) {
      if (!isSearchConfirmed) {
        const [contentLayers = []] = (await app.bus.send(
          'layers/getVisibleLayersByLayoutId',
          legacyBusPayload('content'),
        )) as string[][];
        app.bus.send('layers/hideWithLayoutId', legacyBusPayload('content'));
        app.bus.send('layers/setPreviousLayer', legacyBusPayload(contentLayers[0] || ''));
        app.bus.send('headerOnline/show');
      } else {
        app.bus.send('layers/show', { id: 'SearchResultsHeader' });
      }
    }
  }, [isSearchConfirmed]);

  useEffect(() => {
    const unsub = app.bus.on('env/resize', async () => {
      setisMobileReflow(app.env.isMobileReflow());
      if (app.env.isMobile() && isDesktop) {
        setIsMobile(true);
        await toMobile();
      }
      if (app.env.isDesktop() && isMobile) {
        setIsMobile(false);
        await toDesktop();
      }
    });
    return unsub;
  }, [isDesktop, isMobile, toMobile, toDesktop]);

  const toggleContent = async () => {
    const [contentLayers = []] = (await app.bus.send(
      'layers/getVisibleLayersByLayoutId',
      legacyBusPayload('content'),
    )) as string[][];

    if (content.length > 0) {
      app.bus.send('layers/hideWithLayoutId', legacyBusPayload('content'));
    } else {
      const [previousLayer = ''] = (await app.bus.send('layers/getPreviousLayer')) as string[];
      // default to show category list if there is no previous layer
      app.bus.send('layers/show', {
        id: previousLayer || SEARCH_VIEW_CATEGORY_LIST_ID,
      });
    }

    app.bus.send('layers/setPreviousLayer', legacyBusPayload(contentLayers[0] || ''));
  };

  const topHeader = findByLayoutId('topHeader', children, app); // These are arrays of components
  const map = findByLayoutId('map', children, app);
  const appInfo = findByLayoutId('appInfo', children, app); // this is separated from the map, so that it can be placed later in the DOM for accessibilty
  const content = findByLayoutId('content', children, app);
  const header = findByLayoutId('headerOnline', children, app);
  const floatingBanner = findByLayoutId('floatingBanner', children, app);
  const bottomBar = findByLayoutId('bottomBar', children, app);
  const buttonOptions = findByLayoutId('buttonOptions', children, app);
  const mobileLevelSelector = findByLayoutId('mobileLevelSelector', children, app);
  const modalWindow = findByLayoutId('modalWindow', children, app);
  const mapControls = findByLayoutId('mapControls', children, app);
  const toast = findByLayoutId('toast', children, app);
  const dialog = findByLayoutId('dialog', children, app);
  const feedbackZone = findByLayoutId('feedbackZone', children, app);
  const ariaAnnouncements = findByLayoutId('ariaAnnouncements', children, app);

  const isContentVisible = content.length > 0;

  const levelSelectorDesktop = mapControls.filter(el => isDesktop && el.key === 'widget-mapLevelSelectorOnline');
  // Extract the dedicated mobile map pan control (if any) so we can render it separately
  const mapPanControlMobile = mapControls.find(el => el.key === 'widget-mapPanControlsMobile');
  const filteredMapControls = mapControls.filter(el => el.key !== 'widget-mapPanControlsMobile');

  // isPresentational means that the 'content' section is being used for all of the UI. Happens in POI view and flight details view. Also means that no Header is present in Desktop.
  const isPresentational =
    content.filter(
      el => String(el.key).startsWith('widget-poiView') || String(el.key).startsWith('widget-flightDetails'),
    ).length > 0 || false;

  const isContentFullscreen =
    isMobile &&
    content.filter(
      el =>
        el.key === 'widget-SearchResultsBody' ||
        el.key === 'widget-SearchResultsList' ||
        el.key === 'widget-DirectionsSearchView' ||
        el.key === 'widget-SearchResultsCategoryList' ||
        el.key === 'widget-flightSearchView',
    ).length > 0;

  const [isLevelSelectorOpen, setIsLevelSelectorOpen] = useState(false);

  useEffect(() => {
    const unsubOpen = app.bus.on('mapLevelSelectorOnline/openLevelSelector', () => setIsLevelSelectorOpen(true));
    const unsubClose = app.bus.on('mapLevelSelectorOnline/closeLevelSelector', () => setIsLevelSelectorOpen(false));
    return () => {
      unsubOpen();
      unsubClose();
    };
  }, []);

  const shouldHideSidebar = () => uiHide && ((isDesktop && uiHide.sidebar) || (!isDesktop && uiHide.mobileSearchBar));
  const shouldShowAppInfo = isDesktop || (bottomBar.length === 0 && !isLevelSelectorOpen);
  const shouldShowHeadAndBottomBar = isDesktop || !isLevelSelectorOpen;

  return (
    <Loading hasLoaded={mapLoaded}>
      <Wrapper className={isDesktop ? '-desktop' : '-mobile'} ref={wrapperRef}>
        <TopHeader>{topHeader}</TopHeader>
        <MainWrapper>
          {dialog}
          {isDesktop && toast}
          {isMobile && (
            <MobileToastWrapper $placement={toastPlacement} $topOffset={sidebarHeight}>
              {toast}
            </MobileToastWrapper>
          )}
          <Map
            role="region"
            aria-label={T('fullscreenOnlyLayout:Map · Use arrow keys to pan the map')}
            aria-describedby="MapDescription"
            inert={isMapVisible ? undefined : true}
          >
            {hideMapForVisualTest && <MapCover>visual test map cover</MapCover>}
            {map}
          </Map>
          {mapLoaded && (
            <>
              {!shouldHideSidebar() && (
                <SidebarWrapper
                  data-testid="SidebarWrapper"
                  ref={getSideBarRef}
                  className="sidebar-wrapper"
                  $isFullscreen={isContentFullscreen}
                >
                  <Sidebar
                    $isWide={isPresentational}
                    $isFullscreen={isContentFullscreen}
                    role={isContentFullscreen ? 'dialog' : undefined}
                    aria-modal={isContentFullscreen && !isDesktop ? true : undefined}
                    aria-label={isContentFullscreen ? T('ui:Wayfinding panel') : undefined}
                  >
                    {isHeaderVisible && shouldShowHeadAndBottomBar && (
                      <Header
                        $isContentOpen={isContentVisible}
                        $isPresentational={isPresentational}
                        aria-label={T('fullscreenOnlyLayout:Search Wayfinder on Web')}
                        role="navigation"
                      >
                        {header}
                      </Header>
                    )}
                    <Content $isContentOpen={isContentVisible} $isPresentational={isPresentational}>
                      {content}
                    </Content>
                  </Sidebar>
                  {!isPresentational ? (
                    <Tooltip
                      text={
                        isContentVisible
                          ? T('fullscreenOnlyLayout:Collapse Panel')
                          : T('fullscreenOnlyLayout:Expand Panel')
                      }
                      arrowPosition="top"
                    >
                      <ContentToggler
                        aria-label={
                          isContentVisible
                            ? T('fullscreenOnlyLayout:Collapse Panel')
                            : T('fullscreenOnlyLayout:Expand Panel')
                        }
                        onClick={() => toggleContent()}
                        data-testid="ContentToggler"
                      >
                        <Icon
                          cdn
                          name="chevron-up"
                          width={24}
                          height={24}
                          fillColor={colors.mapNavText}
                          degreesOfIconRotation={isContentVisible ? 0 : 180}
                        />
                      </ContentToggler>
                    </Tooltip>
                  ) : null}
                  {isMobile ? <FloatingBannerWrapper>{floatingBanner}</FloatingBannerWrapper> : <div />}
                </SidebarWrapper>
              )}
              {isMapDisabled && <MapOverlay />}
              {shouldShowHeadAndBottomBar && <BottomBar ref={getBottomBarRef}>{bottomBar}</BottomBar>}
              {isMobile && <MobileLevelSelector>{mobileLevelSelector}</MobileLevelSelector>}
              <ControlsWrapper>
                <LevelSelector $isHidden={uiHide.levelSelector} $isMobile={isMobile}>
                  {levelSelectorDesktop}
                </LevelSelector>
                {!isMobile && (
                  <ButtonOptionsWrapper
                    $isHidden={uiHide.controls}
                    $isMobile={isMobile}
                    style={{ pointerEvents: 'all' }}
                  >
                    <MobileControlsWrapper $isMobileReflow={isMobileReflow}>
                      {buttonOptions.map((el, key: number) => (
                        <MapControlWrapper key={key}>{el}</MapControlWrapper>
                      ))}
                    </MobileControlsWrapper>
                    <FeedbackZoneWrapper>{feedbackZone}</FeedbackZoneWrapper>
                  </ButtonOptionsWrapper>
                )}
              </ControlsWrapper>
              {isMobile && (
                <SpeedDial
                  app={app}
                  isMapVisible={isMapVisible}
                  uiHide={uiHide}
                  isMobileReflow={isMobileReflow}
                  filteredMapControls={filteredMapControls}
                  buttonOptions={buttonOptions}
                  mapPanControlMobile={mapPanControlMobile}
                  feedbackZone={feedbackZone}
                />
              )}
            </>
          )}
          {isMapVisible && shouldShowAppInfo && appInfo}
        </MainWrapper>
        <TooltipAnchor id="tooltip" />
        {modalWindow}
      </Wrapper>
      {ariaAnnouncements}
    </Loading>
  );
};

export default withTheme(FullscreenOnlyLayout);
